/* CGR Theme JS — reveal animations, cart, lightbox */

/* ── Reveal on scroll ── */
function initReveal() {
  const els = document.querySelectorAll('.reveal');
  if (!els.length) return;
  const io = new IntersectionObserver(
    entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }),
    { threshold: 0.12 }
  );
  els.forEach(el => io.observe(el));
}

/* ── Header scroll ── */
function initHeader() {
  const header = document.querySelector('.cgr-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ── Shopify AJAX Cart ── */
async function cartAdd(variantId, qty = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: qty }),
  });
  if (!res.ok) throw new Error('Cart add failed');
  return res.json();
}

async function cartGet() {
  const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  return res.json();
}

async function cartUpdate(updates) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ updates }),
  });
  return res.json();
}

async function cartRemove(lineItemKey) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
  });
  return res.json();
}

/* ── Cart drawer Alpine store ── */
function initCartStore() {
  document.addEventListener('alpine:init', () => {
    Alpine.store('cart', {
      open: false,
      items: [],
      count: 0,

      async load() {
        const data = await cartGet();
        this.items = data.items;
        this.count = data.item_count;
      },

      async add(variantId, qty = 1) {
        await cartAdd(variantId, qty);
        await this.load();
        this.open = true;
      },

      async remove(key) {
        await cartRemove(key);
        await this.load();
      },

      async setQty(key, qty) {
        await cartUpdate({ [key]: qty });
        await this.load();
      },
    });
  });

  document.addEventListener('alpine:initialized', () => {
    Alpine.store('cart').load();
  });
}

/* ── Add-to-cart buttons (non-Alpine fallback) ── */
function initCartButtons() {
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-product-id]');
    if (!btn) return;
    const id = btn.dataset.productId;
    btn.disabled = true;
    try {
      if (typeof Alpine !== 'undefined') {
        await Alpine.store('cart').add(id);
      } else {
        await cartAdd(id);
      }
      btn.textContent = '✓ Added';
      setTimeout(() => { btn.textContent = 'Add to cart'; btn.disabled = false; }, 2000);
    } catch {
      btn.disabled = false;
    }
  });
}

/* ── Lightbox ── */
function initLightbox() {
  const lb = document.getElementById('cgr-lightbox');
  if (!lb) return;

  let images = [];
  let current = 0;

  function open(idx) {
    current = idx;
    render();
    lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    lb.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function nav(d) {
    current = (current + d + images.length) % images.length;
    render();
  }

  function render() {
    const img = images[current];
    if (!img) return;
    lb.querySelector('.cgr-lightbox__img').src = img.src;
    lb.querySelector('.cgr-lightbox__cap-text').textContent = img.caption || '';
    lb.querySelector('.cgr-lightbox__counter').textContent =
      `${String(current + 1).padStart(2, '0')} / ${String(images.length).padStart(2, '0')}`;
  }

  lb.querySelector('.cgr-lightbox__close').addEventListener('click', close);
  lb.querySelector('.cgr-lightbox__nav--prev').addEventListener('click', e => { e.stopPropagation(); nav(-1); });
  lb.querySelector('.cgr-lightbox__nav--next').addEventListener('click', e => { e.stopPropagation(); nav(1); });
  lb.addEventListener('click', close);
  lb.querySelector('.cgr-lightbox__frame').addEventListener('click', e => e.stopPropagation());

  window.addEventListener('keydown', e => {
    if (!lb.classList.contains('is-open')) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') nav(-1);
    if (e.key === 'ArrowRight') nav(1);
  });

  /* attach to gallery buttons */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-lightbox-src]');
    if (!btn) return;
    const gallery = btn.closest('[data-lightbox-gallery]');
    if (gallery) {
      images = [...gallery.querySelectorAll('[data-lightbox-src]')].map(el => ({
        src: el.dataset.lightboxSrc,
        caption: el.dataset.lightboxCaption || '',
      }));
      open(parseInt(btn.dataset.lightboxIndex || 0));
    }
  });
}

/* ── Tab switcher ── */
function initTabs() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-tab]');
    if (!btn) return;
    const group = btn.closest('[data-tabs]');
    if (!group) return;
    const target = btn.dataset.tab;

    group.querySelectorAll('[data-tab]').forEach(b => b.classList.toggle('is-on', b.dataset.tab === target));
    group.querySelectorAll('[data-tab-panel]').forEach(p => {
      p.hidden = p.dataset.tabPanel !== target;
    });
  });
}

/* ── Product image viewer ── */
function initProductGallery() {
  document.addEventListener('click', e => {
    const thumb = e.target.closest('.cgr-prod-imgs__thumb');
    if (!thumb) return;
    const container = thumb.closest('.cgr-prod-imgs');
    const mainImg = container.querySelector('.cgr-prod-imgs__photo');
    if (mainImg) mainImg.src = thumb.querySelector('img').src;
    container.querySelectorAll('.cgr-prod-imgs__thumb').forEach(t => t.classList.remove('is-on'));
    thumb.classList.add('is-on');
  });
}

/* ── Shop dual filter (category × residency) ── */
function initShopFilter() {
  const container = document.querySelector('[data-shop-filter]');
  if (!container) return;

  const { catMap, resMap } = window.__shopFilterMaps || { catMap: {}, resMap: {} };

  let activeCat = 'all';
  let activeRes = 'all';

  const catBtns = container.querySelectorAll('[data-cat-btn]');
  const resBtns = container.querySelectorAll('[data-res-btn]');
  const items   = container.querySelectorAll('.cgr-shop__item');
  const empty   = container.querySelector('[data-shop-empty]');

  function applyFilter() {
    let visible = 0;
    items.forEach(item => {
      const id   = item.dataset.productId;
      const cats = catMap[id] || [];
      const res  = resMap[id] || '';
      const catOk = activeCat === 'all' || cats.includes(activeCat);
      const resOk = activeRes === 'all' || res === activeRes;
      item.hidden = !(catOk && resOk);
      if (!item.hidden) visible++;
    });
    if (empty) empty.hidden = visible > 0;
  }

  catBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      activeCat = btn.dataset.catBtn;
      catBtns.forEach(b => b.classList.toggle('is-on', b === btn));
      applyFilter();
    });
  });

  resBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (activeRes === btn.dataset.resBtn) {
        activeRes = 'all';
        resBtns.forEach(b => b.classList.remove('is-on'));
      } else {
        activeRes = btn.dataset.resBtn;
        resBtns.forEach(b => b.classList.toggle('is-on', b === btn));
      }
      applyFilter();
    });
  });
}

/* ── Typo-loop Hero ── */
function initTypoLoop() {
  const stage = document.querySelector('[data-typo-loop-stage]');
  if (!stage) return;
  const enEl  = stage.querySelector('.cgr-hero__phrase--en');
  const resEl = stage.querySelector('.cgr-hero__phrase--resonance');
  if (!enEl || !resEl) return;

  /* entry → en (full) → en-out → resonance (single word) → resonance-out → loop */
  const steps = [
    ['entry',          900],
    ['en',            3400],
    ['en-out',         700],
    ['resonance',     3400],
    ['resonance-out',  700],
  ];
  let i = 0;

  function applyPhase(ph) {
    enEl.classList.toggle('is-in',  ph === 'en');
    enEl.classList.toggle('is-out', ph === 'en-out');
    resEl.classList.toggle('is-in',  ph === 'resonance');
    resEl.classList.toggle('is-out', ph === 'resonance-out');
  }

  function tick() {
    const [ph, dur] = steps[i];
    applyPhase(ph);
    i = i >= steps.length - 1 ? 1 : i + 1;
    setTimeout(tick, dur);
  }
  tick();
}

/* ── Init ── */
// initCartStore must run immediately (not in DOMContentLoaded) so that the
// alpine:init listener is registered before Alpine.js executes.
initCartStore();

document.addEventListener('DOMContentLoaded', () => {
  initReveal();
  initHeader();
  initCartButtons();
  initLightbox();
  initTabs();
  initProductGallery();
  initShopFilter();
  initTypoLoop();
});
